import React, { useEffect, useState } from 'react';
import Modal from 'components/elements/Modal';
import { CSVLink } from 'react-csv';
import VisitorService, { VisitorProps } from 'services/hooks/visitor-service';
import { useQuery } from 'react-query';
import { Controller, useForm } from 'react-hook-form';
import InputMonthYearPicker from 'components/elements/Form/Input/inputMonthYearPicker';
import ExportIcon from 'assets/Icons/export/ExportIcon';
import { isDateWithinRange } from 'utils/time';

const headers = [
  { label: 'First Name', key: 'firstName' },
  { label: 'Last Name', key: 'lastName' },
  { label: 'Phone Number', key: 'phoneNumber' },
  { label: 'Reason', key: 'reason' },
  { label: 'Tag', key: 'tag' },
  { label: 'Company', key: 'company' },
  { label: 'Email', key: 'email' },
  { label: 'Date', key: 'createdAt' }
];

export default function ExportCSV({ showModal, setShowModal, entityName }: any) {
  const [getVisitorsResult, setGetVisitorsResult] = useState<any>([]);
  const [filteredVisitors, setFilteredVisitors] = useState<any>([]);

  const { refetch, data: visitorsData } = useQuery<VisitorProps[], Error>(
    'visitors',
    VisitorService.findAllVisitors,
    {
      onError: (err: any) => {
        console.log(err.response?.data || err);
      }
    }
  );

  useEffect(() => {
    if (visitorsData && Array.isArray(visitorsData)) {
      refetch();
      setGetVisitorsResult(visitorsData);
    }
  }, [visitorsData, refetch]);

  const methods = useForm({
    defaultValues: {
      monthRange: []
    }
  });

  const monthRangeWatch = methods.watch('monthRange');
  const [getMonthRange, setGetMonthRange] = useState<any>(undefined);

  useEffect(() => {
    if (monthRangeWatch) {
      setGetMonthRange(monthRangeWatch);
    }
  }, [monthRangeWatch]);

  // Filter the data based on selected month range and select specific fields
  useEffect(() => {
    if (getMonthRange?.startDate || getMonthRange?.endDate) {
      const filteredData = getVisitorsResult
        .filter((visitor: VisitorProps) => {
          const createdAtDate = new Date(visitor.createdAt);
          const startOfMonth = getMonthRange?.startDate ? new Date(getMonthRange?.startDate) : null;
          const endOfMonth = getMonthRange?.endDate ? new Date(getMonthRange?.endDate) : null;

          // Check if both Start and End dates were provided as filter inputs
          if (startOfMonth && endOfMonth) {
            // Check if createdAt date falls within the selected start and end month range
            return isDateWithinRange({ startOfMonth, endOfMonth, createdAtDate });
          } else if (startOfMonth) {
            // Filter for the entire month of the startDate (mm/yyyy)
            return (
              createdAtDate.getMonth() == startOfMonth.getMonth() &&
              createdAtDate.getFullYear() == startOfMonth.getFullYear()
            );
          } else if (endOfMonth) {
            // Filter for the entire month of the endDate (mm/yyyy)
            return (
              createdAtDate.getMonth() == endOfMonth.getMonth() &&
              createdAtDate.getFullYear() == endOfMonth.getFullYear()
            );
          }
          return false;
        })
        .map((filteredItem: VisitorProps) => ({
          firstName: filteredItem?.firstName,
          lastName: filteredItem?.lastName,
          phoneNumber: filteredItem?.phoneNumber,
          reason: filteredItem?.reason,
          tag: filteredItem?.tag,
          company: filteredItem?.company,
          email: filteredItem?.email,
          createdAt: filteredItem?.createdAt
        }));

      setFilteredVisitors(filteredData);
    } else {
      // When a date range has not been specified, export all visitors that the end user sees
      const allVisitorsData = getVisitorsResult.map((filteredItem: VisitorProps) => ({
        firstName: filteredItem?.firstName,
        lastName: filteredItem?.lastName,
        phoneNumber: filteredItem?.phoneNumber,
        reason: filteredItem?.reason,
        tag: filteredItem?.tag,
        company: filteredItem?.company,
        email: filteredItem?.email,
        createdAt: filteredItem?.createdAt
      }));

      setFilteredVisitors(allVisitorsData);
    }
  }, [getMonthRange, getVisitorsResult]);

  function generateFilename() {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0'); // Adding 1 to month because it's zero-based
    const day = String(currentDate.getDate()).padStart(2, '0');
    const hours = String(currentDate.getHours()).padStart(2, '0');
    const minutes = String(currentDate.getMinutes()).padStart(2, '0');
    const seconds = String(currentDate.getSeconds()).padStart(2, '0');

    return `visitorsCSV_${year}-${month}-${day}_${hours}.${minutes}.${seconds}.csv`;
  }

  const handleOnClick = () => {
    setShowModal(false);
  };

  return (
    <Modal
      isOpen={showModal}
      onRequestClose={() => {
        setShowModal(false);
        // if (setEditUUID) setEditUUID('');
      }}
      title={entityName}
    >
      <div className="col-span-2 mt-3">
        <div className="grid grid-cols-2 gap-4">
          <InputMonthYearPicker
            Controller={Controller}
            control={methods.control}
            label="Select Month Range"
            name="monthRange"
            placeholderStart="Select Start Month"
            placeholderEnd="Select End Month"
            isClearable
          />
        </div>
      </div>
      {
        <CSVLink
          data={filteredVisitors}
          filename={generateFilename()}
          className={`primary-btn default mt-4 px-6 w-fit gap-2`}
          headers={headers}
          onClick={handleOnClick}
        >
          {<ExportIcon />} Export CSV
        </CSVLink>
      }
    </Modal>
  );
}
